/**
 * Common UI rendering helpers for highlight buttons, controls, and notes
 */

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useExperienceStore } from '@/store/experienceStore';
import { TrashBinTrashOutline } from '@assessprep/icons/solar';
import { MessageSquare01Line, MinusLine, XLine } from '@assessprep/icons/untitled';
import React from 'react';
import { createRoot } from 'react-dom/client';

const createReactElement = (type: any, props: any, children?: any) =>
  React.createElement(type, props, children);

// iPad row 4 (touch mode): ONE delegated document click listener + the single
// "currently open" wrapper — a heavy annotator would otherwise accrue one
// document listener PER highlight, all running contains-walks on every tap
// (the highest-frequency input on the weakest hardware). Tap a highlight to
// open its buttons (closing the previously open one); tap outside to dismiss.
type OpenTouchHighlight = { wrapper: HTMLElement; container: HTMLElement };
let openTouchHighlight: OpenTouchHighlight | null = null;
let touchHighlightCount = 0;

const dismissOpenTouchHighlight = (e: Event) => {
  const open = openTouchHighlight;
  if (!open) return;
  // Self-heal if the open wrapper was removed without _cleanup.
  if (!document.body.contains(open.wrapper)) {
    (open.wrapper as any)._cleanup?.();
    openTouchHighlight = null;
    return;
  }
  const target = e.target as Node;
  if (!open.container.contains(target) && !open.wrapper.contains(target)) {
    open.wrapper.style.display = 'none';
    openTouchHighlight = null;
  }
};

const registerTouchHighlight = () => {
  touchHighlightCount += 1;
  if (touchHighlightCount === 1) {
    document.addEventListener('click', dismissOpenTouchHighlight);
  }
};

const releaseTouchHighlight = (wrapper: HTMLElement) => {
  touchHighlightCount = Math.max(0, touchHighlightCount - 1);
  if (openTouchHighlight?.wrapper === wrapper) openTouchHighlight = null;
  if (touchHighlightCount === 0) {
    document.removeEventListener('click', dismissOpenTouchHighlight);
  }
};

/**
 * Safely remove a DOM element with proper checks
 */
const safeRemoveElement = (element: Element | null | undefined) => {
  if (!element) return;
  try {
    // Check if element is still in the DOM
    if (document.body.contains(element)) {
      // Use remove() method which is safer
      element.remove();
    }
  } catch (error) {
    console.warn('Error removing element:', error);
  }
};

/**
 * Safely remove a minimized note with proper cleanup of event listeners
 */
export const safeRemoveMinimizedNote = (note: Element | null | undefined) => {
  if (!note) return;
  try {
    // Clean up container event listeners if they exist
    if ((note as any)._containerCleanup) {
      (note as any)._containerCleanup();
    }
    // Remove the element
    safeRemoveElement(note);
  } catch (error) {
    console.warn('Error removing minimized note:', error);
  }
};

/**
 * Creates action buttons (message + remove) for highlights
 */
export const createActionButtons = (params: {
  container: HTMLElement;
  onMessage: () => void;
  onRemove: () => void;
  commentsVisible: boolean;
  hasNote?: boolean;
}) => {
  const { container, onMessage, onRemove, commentsVisible, hasNote } = params;

  const wrapper = document.createElement('div');
  wrapper.className = container.classList.contains('image-highlight-overlay')
    ? 'image-highlight-buttons-wrapper'
    : 'highlight-buttons-wrapper';
  wrapper.style.pointerEvents = 'auto';
  wrapper.style.display = 'none';

  const root = createRoot(wrapper);

  const ButtonsComponent = () => {
    const handleMessageClick = (e: any) => {
      e.preventDefault();
      e.stopPropagation();
      const currentCommentsVisible = useExperienceStore.getState().commentsVisible ?? true;
      if (currentCommentsVisible) onMessage();
    };

    const handleRemoveClick = (e: any) => {
      e.preventDefault();
      e.stopPropagation();
      onRemove();
    };

    const buttons = [];
    const isImageHighlight = container.classList.contains('image-highlight-overlay');

    // Only show message button when note is available AND commentsVisible is false
    const showMessageButton = !hasNote || !commentsVisible;

    if (showMessageButton) {
      buttons.push(
        createReactElement(
          Button,
          {
            key: 'message',
            type: 'button',
            size: 'icon-sm',
            className:
              'rounded-full ' +
              (isImageHighlight ? 'image-highlight-message-btn' : 'highlight-message-btn'),
            onClick: handleMessageClick,
          },
          React.createElement(MessageSquare01Line)
        )
      );
    }

    // Always show remove button
    buttons.push(
      createReactElement(
        Button,
        {
          key: 'remove',
          type: 'button',
          size: 'icon-sm',
          className:
            'rounded-full ' +
            (isImageHighlight ? 'image-highlight-remove-btn' : 'highlight-remove-btn'),
          onClick: handleRemoveClick,
        },
        React.createElement(XLine)
      )
    );

    // For image highlights: when message button is hidden, add class to shift remove button position
    const containerClassName = isImageHighlight
      ? 'image-highlight-buttons-container' + (!showMessageButton ? ' single-button' : '')
      : 'highlight-buttons-container';

    return createReactElement(
      'div',
      {
        className: containerClassName,
      },
      buttons
    );
  };

  root.render(React.createElement(ButtonsComponent));
  container.appendChild(wrapper);

  const showButtons = () => {
    if (commentsVisible) wrapper.style.display = 'flex';
  };

  const hideButtons = (e: any) => {
    if (!wrapper.contains(e.relatedTarget)) {
      wrapper.style.display = 'none';
    }
  };

  // iPad row 4: no hover on touch — tap the highlight to show the buttons,
  // tap anywhere outside to dismiss (the CSS :hover reveal is
  // @media (hover: hover) gated to match). Outside-dismiss is DELEGATED: one
  // shared document listener checks only the currently open wrapper (O(1) per
  // tap, not O(highlights) — see dismissOpenTouchHighlight above).
  const touchOnly = window.matchMedia?.('(hover: none)').matches ?? false;

  const toggleButtons = (e: Event) => {
    if (wrapper.contains(e.target as Node)) return;
    // Parity with the hover-path hide rule: no buttons while a note editor is open.
    if (document.querySelector('.note-editing-tooltip')) return;
    if (wrapper.style.display === 'flex') {
      wrapper.style.display = 'none';
      if (openTouchHighlight?.wrapper === wrapper) openTouchHighlight = null;
    } else {
      // Opening this one closes the previously open one.
      if (openTouchHighlight && openTouchHighlight.wrapper !== wrapper) {
        openTouchHighlight.wrapper.style.display = 'none';
        openTouchHighlight = null;
      }
      showButtons();
      if (wrapper.style.display === 'flex') openTouchHighlight = { wrapper, container };
    }
  };

  if (touchOnly) {
    container.addEventListener('click', toggleButtons);
    registerTouchHighlight();
  } else {
    container.addEventListener('mouseenter', showButtons);
    container.addEventListener('mouseleave', hideButtons);
    wrapper.addEventListener('mouseenter', showButtons);
    wrapper.addEventListener('mouseleave', hideButtons);
  }

  // Expose cleanup for caller (idempotent — the delegated-listener registry
  // must not double-release when the self-heal and a caller both clean up).
  let cleaned = false;
  (wrapper as any)._cleanup = () => {
    if (cleaned) return;
    cleaned = true;
    if (touchOnly) {
      container.removeEventListener('click', toggleButtons);
      releaseTouchHighlight(wrapper);
    } else {
      container.removeEventListener('mouseenter', showButtons);
      container.removeEventListener('mouseleave', hideButtons);
      wrapper.removeEventListener('mouseenter', showButtons);
      wrapper.removeEventListener('mouseleave', hideButtons);
    }
  };

  return wrapper;
};

/**
 * Updates visibility of notes and buttons based on commentsVisible state
 */
export const updateNotesVisibility = (htmlRef: HTMLElement, commentsVisible: boolean) => {
  // Show/hide minimized notes
  const notes = htmlRef.querySelectorAll('.minimized-note-display');
  notes.forEach((note: any) => {
    note.style.display = commentsVisible ? 'block' : 'none';
    if (commentsVisible) note.style.opacity = '0.5';
  });

  // Reset button wrappers (they show on hover)
  const buttonWrappers = htmlRef.querySelectorAll(
    '.highlight-buttons-wrapper, .image-highlight-buttons-wrapper'
  );
  buttonWrappers.forEach((wrapper: any) => {
    wrapper.style.display = 'none';
  });
};

/**
 * Creates minimized note display (notepad) for highlights
 */
export const createMinimizedNote = (
  container: HTMLElement,
  noteContent: string,
  onOpenEditor: () => void
) => {
  const existingNote = container.querySelector('.minimized-note-display');
  if (existingNote) safeRemoveMinimizedNote(existingNote);

  if (!noteContent.trim()) return;

  const minimizedNote = document.createElement('div');
  minimizedNote.className = 'minimized-note-display';
  // minimizedNote.style.cssText = `
  //   position: absolute; top: -45px; left: 50%; transform: translateX(-50%);
  //   background: #fffbea; border: 2px solid #f59e0b; border-radius: 8px;
  //   padding: 6px 8px; max-width: 200px; min-width: 120px;
  //   box-shadow: 0 2px 8px rgba(0,0,0,0.1); z-index: 1000;
  //   cursor: pointer; font-size: 12px; line-height: 1.4; color: #374151;
  //   word-wrap: break-word; display: block; opacity: 0.5;
  //   transition: opacity 0.3s ease; max-height: 36px; pointer-events: auto;
  // `;

  // Arrow pointing down
  const arrow = document.createElement('div');
  arrow.style.cssText = `
    position: absolute; bottom: -8px; left: 50%; transform: translateX(-50%);
    width: 0; height: 0; border-left: 8px solid transparent;
    border-right: 8px solid transparent; border-top: 8px solid #f59e0b;
  `;
  minimizedNote.appendChild(arrow);

  // Note text — full note; CSS line-clamps it to the pill on first open (the
  // old 10-char JS cut collapsed 140-char notes to "This is a ...").
  const noteText = document.createElement('div');
  noteText.textContent = noteContent;
  minimizedNote.title = noteContent;
  minimizedNote.appendChild(noteText);

  // Hover effects on the note itself
  const handleNoteEnter = () => (minimizedNote.style.opacity = '1');
  const handleNoteLeave = () => (minimizedNote.style.opacity = '0.5');
  const handleNoteClick = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onOpenEditor();
  };

  minimizedNote.addEventListener('mouseenter', handleNoteEnter);
  minimizedNote.addEventListener('mouseleave', handleNoteLeave);
  minimizedNote.addEventListener('click', handleNoteClick);

  // Hover effects on the container (highlighted text or image overlay)
  const handleContainerEnter = () => {
    if (minimizedNote && minimizedNote.style.display !== 'none') {
      minimizedNote.style.opacity = '1';
    }
  };
  const handleContainerLeave = () => {
    if (minimizedNote && minimizedNote.style.display !== 'none') {
      minimizedNote.style.opacity = '0.5';
    }
  };

  container.addEventListener('mouseenter', handleContainerEnter);
  container.addEventListener('mouseleave', handleContainerLeave);

  // Store cleanup function to remove all listeners
  (minimizedNote as any)._containerCleanup = () => {
    minimizedNote.removeEventListener('mouseenter', handleNoteEnter);
    minimizedNote.removeEventListener('mouseleave', handleNoteLeave);
    minimizedNote.removeEventListener('click', handleNoteClick);
    container.removeEventListener('mouseenter', handleContainerEnter);
    container.removeEventListener('mouseleave', handleContainerLeave);
  };

  container.appendChild(minimizedNote);
  return minimizedNote;
};

/**
 * Creates note editing tooltip (notepad interface)
 */
export const createNoteEditor = (params: {
  position: { x: number; y: number };
  currentNote: string;
  highlightId: string;
  onSave: (note: string) => void;
  onDelete: () => void;
  onClose: () => void;
  /** R2-G56: live anchor recompute — the editor re-positions on window resize
   *  instead of floating at the stale open-time coordinates. */
  getPosition?: () => { x: number; y: number };
}) => {
  const { position, currentNote, highlightId, onSave, onDelete, onClose, getPosition } = params;

  // Note: Existing tooltips are now removed by the caller (openTextNoteEditor/openImageNoteEditor)
  // with proper minimized note restoration

  const tooltip = document.createElement('div');
  tooltip.className = 'note-editing-tooltip';
  tooltip.setAttribute('data-highlight-id', highlightId);
  tooltip.style.cssText = `
    position: absolute; left: ${position.x}px; top: ${position.y}px;
    z-index: 20; background: #fffbea; border: 2px solid #f59e0b;
    border-radius: 6px; box-shadow: 0 4px 12px rgba(0,0,0,0.12);
    min-width: 230px; max-width: 230px; opacity: 0.9;
  `;
  // z-index 20, deliberately below every chrome layer (APL-11850 §27): the
  // editor is body-mounted so no container isolation can trap it — at its old
  // 95 it painted over the sticky exam header (30), the tools strip / offline
  // banner (40), and even Radix dialogs and sheets (50). 20 keeps it above all
  // exam content (statics + the isolated highlight spans) and under chrome.

  const root = createRoot(tooltip);

  const EditorComponent = () => {
    const [noteValue, setNoteValue] = React.useState(currentNote);
    const lastSaved = React.useRef(currentNote);
    const debounceTimer = React.useRef<NodeJS.Timeout | null>(null);

    const handleSave = React.useCallback((value: string) => {
      if (value === lastSaved.current) return;
      lastSaved.current = value;
      onSave(value);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Debounced save - calls handleSave after user stops typing for 500ms
    const debouncedSave = React.useCallback(
      (value: string) => {
        if (debounceTimer.current) {
          clearTimeout(debounceTimer.current);
        }
        debounceTimer.current = setTimeout(() => {
          handleSave(value);
        }, 500);
      },
      [handleSave]
    );

    // Cleanup debounce timer on unmount
    React.useEffect(() => {
      return () => {
        if (debounceTimer.current) {
          clearTimeout(debounceTimer.current);
        }
      };
    }, []);

    const handleBlur = (e: any) => {
      // Cancel any pending debounced save
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      // Save immediately on blur
      handleSave(e.target.value.trim());
    };

    const handleChange = (e: any) => {
      const value = e.target.value;
      if (value.split('\n').length <= 10) {
        setNoteValue(value);
        e.target.style.height = 'auto';
        e.target.style.height = Math.min(e.target.scrollHeight, 140) + 'px';
        // Debounced auto-save - only saves after 500ms of no typing
        debouncedSave(value.trim());
      }
    };

    const handleMinimize = (e: any) => {
      e.preventDefault();
      e.stopPropagation();
      // Clean up and remove tooltip safely
      try {
        if ((tooltip as any)._cleanup) {
          (tooltip as any)._cleanup();
        }
        safeRemoveElement(tooltip);
      } catch (error) {
        console.warn('Error minimizing tooltip:', error);
      }
      // Call onClose to show minimized note
      onClose();
    };

    return createReactElement('div', { className: 'note-editing-content' }, [
      // Header with controls
      createReactElement(
        'div',
        {
          key: 'header',
          style: {
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            padding: '6px 8px 4px 8px',
            borderBottom: '1px solid rgba(245, 158, 11, 0.2)',
            gap: '4px',
          },
        },
        [
          createReactElement(
            Button,
            {
              key: 'minimize',
              type: 'button',
              variant: 'ghost',
              size: 'icon-sm',
              onClick: handleMinimize,
              title: 'Minimize',
              style: { color: '#f59e0b' },
            },
            React.createElement(MinusLine, { fontSize: 14 })
          ),
          createReactElement(
            Button,
            {
              key: 'delete',
              type: 'button',
              variant: 'ghost',
              size: 'icon-sm',
              // J21-B099: stays enabled on an emptied note so the comment can
              // still be cleared and the editor closed.
              onClick: onDelete,
              title: 'Delete comment',
              style: { color: 'var(--hl-note-accent)' },
            },
            React.createElement(TrashBinTrashOutline, { fontSize: 14 })
          ),
        ]
      ),
      // Textarea
      createReactElement('div', { key: 'input', style: { padding: '4px' } }, [
        createReactElement(Textarea, {
          key: 'textarea',
          value: noteValue,
          placeholder: 'Add note..',
          rows: 2,
          autoFocus: true,
          onChange: handleChange,
          onBlur: handleBlur,
          // APL-12321 follow-up: this highlight-comment note is a plain
          // <textarea> the rich-text fix never reached — Safari 18+ could
          // still ghost-suggest text here while a student is annotating.
          writingsuggestions: 'false',
          className: 'min-h-0 border-none bg-transparent shadow-none focus-visible:ring-0',
          style: {
            fontSize: '13px',
            resize: 'none',
            outline: 'none',
            lineHeight: '1.5',
            color: '#1f2937',
          },
        }),
      ]),
    ]);
  };

  root.render(React.createElement(EditorComponent));
  document.body.appendChild(tooltip);

  // Close on click outside
  let clickListenerActive = false;
  const handleClickOutside = (e: MouseEvent) => {
    if (!tooltip.contains(e.target as Node) && document.body.contains(tooltip)) {
      // Full cleanup (click + resize listeners) — assigned below, before this fires.
      (tooltip as any)._cleanup?.();
      try {
        safeRemoveElement(tooltip);
        onClose();
      } catch (error) {
        console.warn('Error closing tooltip:', error);
      }
    }
  };

  setTimeout(() => {
    if (!clickListenerActive) {
      document.addEventListener('click', handleClickOutside);
      clickListenerActive = true;
    }
  }, 50);

  // R2-G56: reanchor on window resize — the anchor span/overlay reflows but
  // the body-absolute tooltip would keep its open-time coordinates.
  const handleWindowResize = getPosition
    ? () => {
        try {
          const next = getPosition();
          tooltip.style.left = `${next.x}px`;
          tooltip.style.top = `${next.y}px`;
        } catch (error) {
          console.warn('Error repositioning note editor on resize:', error);
        }
      }
    : null;
  if (handleWindowResize) window.addEventListener('resize', handleWindowResize);

  // Store cleanup function
  (tooltip as any)._cleanup = () => {
    if (clickListenerActive) {
      document.removeEventListener('click', handleClickOutside);
      clickListenerActive = false;
    }
    if (handleWindowResize) window.removeEventListener('resize', handleWindowResize);
  };

  return tooltip;
};

/**
 * Calculate smart tooltip position to keep it within viewport
 */
export const calculateTooltipPosition = (
  rect: DOMRect,
  tooltipConfig: {
    width: number;
    height: number;
    xOffset: number;
    yOffset: number;
    fallbackY: number;
  }
) => {
  const { width, height, xOffset, yOffset, fallbackY } = tooltipConfig;
  const viewportWidth = window.innerWidth;

  let x = rect.left + xOffset + window.scrollX;
  let y = rect.top - height + yOffset + window.scrollY;

  // Adjust X if off-screen
  if (x + width > viewportWidth + window.scrollX) {
    x = viewportWidth - width - 10 + window.scrollX;
  }
  if (x < window.scrollX) {
    x = 10 + window.scrollX;
  }

  // Adjust Y if off-screen
  if (y < window.scrollY) {
    y = rect.bottom + fallbackY + window.scrollY;
  }

  return { x, y };
};
